import { isDefined } from "@clipboard-health/util-ts";
import { Paper, Popover, Stack } from "@mui/material";
import { Text, useModalState } from "@src/appV2/redesign/system/ui-components";
import { useMemo, useState } from "react";

import { APP_V2_USER_EVENTS, logEvent } from "../lib/analytics";
import { Button } from "../redesign/components/Button";
import { LoadingButton } from "../redesign/components/LoadingButton";
import { useDefinedWorker } from "../Worker/useDefinedWorker";
import { useCreateNewsFeedItemReaction } from "./api/useCreateNewsFeedItemReaction";
import { NewsFeedItemReactionEmojiIcon } from "./ReactionEmojiIcon";
import { NewsFeedItemReactionsCount } from "./ReactionsCount";
import { type NewsFeedItemReaction, NewsFeedItemReactionEmoji } from "./types";

interface NewsFeedItemReactionsButtonProps {
  newsFeedItemId: string;
  reactions: NewsFeedItemReaction[];
}

const MAX_REACTION_COUNT = 3;

export function NewsFeedItemReactionsButton(props: NewsFeedItemReactionsButtonProps) {
  const { newsFeedItemId, reactions } = props;
  const modalState = useModalState();
  const [anchorElement, setAnchorElement] = useState<HTMLButtonElement>();
  const { mutateAsync, isLoading } = useCreateNewsFeedItemReaction(newsFeedItemId, {
    meta: {
      userSuccessMessage: "Your reaction has been added!",
      userErrorMessage: "Error adding your reaction.",
    },
  });

  const currentWorker = useDefinedWorker();

  const currentUserReaction = useMemo(
    () => reactions.find((reaction) => reaction.workerId === currentWorker.userId),
    [currentWorker.userId, reactions]
  );
  const hasUserReactedAlready = isDefined(currentUserReaction);

  return (
    <>
      <Button
        variant="outlined"
        disabled={isLoading}
        onClick={(event) => {
          setAnchorElement(event.currentTarget);
          modalState.openModal();
        }}
      >
        <Stack direction="row" spacing={1}>
          <Stack direction="row" spacing={0}>
            {reactions.slice(0, MAX_REACTION_COUNT).map((reaction) => (
              <NewsFeedItemReactionEmojiIcon key={reaction.id} emoji={reaction.emojiType} />
            ))}
          </Stack>
          <NewsFeedItemReactionsCount
            includeText
            showIcon={reactions.length === 0}
            count={reactions.length}
          />
        </Stack>
      </Button>
      <Popover
        open={modalState.modalIsOpen}
        anchorEl={anchorElement}
        anchorOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
        transformOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
        elevation={6}
        onClose={modalState.closeModal}
      >
        <Paper component={Stack} justifyContent="center" alignItems="center" p={3} spacing={2}>
          {hasUserReactedAlready ? (
            <Text>You&apos;ve reacted to this message!</Text>
          ) : (
            <Text>Add your own reaction:</Text>
          )}
          <Stack direction="row" spacing={1}>
            {[
              NewsFeedItemReactionEmoji.AWARD_BUNNY,
              NewsFeedItemReactionEmoji.CHAMPAGNE_BUNNY,
              NewsFeedItemReactionEmoji.HEART_BUNNY,
              NewsFeedItemReactionEmoji.MONEY_BUNNY,
            ].map((emojiType) => (
              <LoadingButton
                key={emojiType}
                variant="outlined"
                disabled={
                  isLoading ||
                  (hasUserReactedAlready && currentUserReaction.emojiType !== emojiType)
                }
                isLoading={isLoading}
                onClick={async () => {
                  if (hasUserReactedAlready) {
                    return;
                  }

                  await mutateAsync({
                    data: { type: "news-feed-item-reaction", attributes: { emojiType } },
                  });
                  logEvent(APP_V2_USER_EVENTS.NEWS_FEED_REACTION_ADDED, {
                    workerId: currentWorker.userId,
                    emojiType,
                  });
                  modalState.closeModal();
                }}
              >
                <NewsFeedItemReactionEmojiIcon emoji={emojiType} />
              </LoadingButton>
            ))}
          </Stack>
        </Paper>
      </Popover>
    </>
  );
}
